import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOrder, callWaiter } from '../../services/api';

const C = {
  bg:'#060b14', surface:'#0c1525', card:'#101d34',
  border:'rgba(255,255,255,0.06)', primary:'#00c896',
  blue:'#3b82f6', warning:'#f59e0b', danger:'#ef4444',
  price:'#4ade80', text:'#e8f1ff', text2:'#7a94c0', text3:'#3a5070',
};

const STEPS = [
  { key:'pending',   label:'Alındı',       icon:'🧾', desc:'Sifarişiniz qəbul edildi' },
  { key:'confirmed', label:'Qəbul edildi', icon:'✅', desc:'Mətbəx başladı' },
  { key:'preparing', label:'Hazırlanır',   icon:'👨‍🍳', desc:'Hazırlanma prosesindədir' },
  { key:'ready',     label:'Hazırdır',     icon:'🔔', desc:'Sifarişiniz hazırdır!' },
  { key:'served',    label:'Verildi',      icon:'🎉', desc:'Nuş olsun!' },
];

function curIdx(status) {
  const i = STEPS.findIndex(s => s.key === status);
  return i === -1 ? 0 : i;
}

const COOLDOWN_MS = 3 * 60 * 1000;

function PulseRing() {
  return (
    <>
      <div style={{ width:10, height:10, borderRadius:'50%', background:C.blue,
        animation:'pulse 1.4s ease infinite' }} />
      <style>{`@keyframes pulse{0%{transform:scale(1);opacity:1}50%{transform:scale(1.7);opacity:.4}100%{transform:scale(1);opacity:1}}`}</style>
    </>
  );
}

export default function OrderStatusPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [waiterCalled, setWaiterCalled] = useState(false);
  const [waiterCooldown, setWaiterCooldown] = useState(0);
  const [waiterLoading, setWaiterLoading]   = useState(false);

  const pollRef     = useRef(null);
  const coolRef     = useRef(null);

  const fetchOrder = useCallback(async () => {
    try {
      const d = await getOrder(orderId);
      const o = Array.isArray(d) ? d[0] : (d?.data || d);
      setOrder(o); setLoading(false);
      if (o?.status === 'served') clearInterval(pollRef.current);
    } catch (e) { setError(e?.message || 'Sifariş tapılmadı'); setLoading(false); }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
    pollRef.current = setInterval(fetchOrder, 8000);
    return () => clearInterval(pollRef.current);
  }, [fetchOrder]);

  const handleCallWaiter = async () => {
    if (waiterLoading || waiterCooldown > 0) return;
    setWaiterLoading(true);
    try {
      await callWaiter(orderId);
      setWaiterCalled(true);
      setWaiterCooldown(COOLDOWN_MS);
      const t0 = Date.now();
      coolRef.current = setInterval(() => {
        const left = COOLDOWN_MS - (Date.now() - t0);
        if (left <= 0) { clearInterval(coolRef.current); setWaiterCooldown(0); }
        else setWaiterCooldown(left);
      }, 1000);
    } catch { alert('Çağırış uğursuz oldu. Yenidən cəhd edin.'); }
    finally { setWaiterLoading(false); }
  };

  useEffect(() => () => { clearInterval(coolRef.current); }, []);

  const fmtCool = ms => `${Math.ceil(ms/1000)}s`;

  // ── Loading ──
  if (loading) return (
    <div style={{ background:C.bg, minHeight:'100vh', display:'flex',
      alignItems:'center', justifyContent:'center', flexDirection:'column', gap:12 }}>
      <div style={{ fontSize:32, animation:'spin 1.2s linear infinite', display:'inline-block', color:C.primary }}>⟳</div>
      <div style={{ fontSize:13, color:C.text2 }}>Yüklənir...</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  // ── Error ──
  if (error || !order) return (
    <div style={{ background:C.bg, minHeight:'100vh', display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center', padding:'24px', textAlign:'center' }}>
      <div style={{ fontSize:52, marginBottom:16 }}>😕</div>
      <div style={{ fontSize:18, fontWeight:800, color:C.text, marginBottom:8 }}>Sifariş tapılmadı</div>
      <div style={{ fontSize:13, color:C.text2, marginBottom:24 }}>{error}</div>
      <button onClick={() => navigate(-1)} style={{
        background:C.primary, border:'none', borderRadius:12, color:'#fff',
        padding:'12px 28px', fontSize:14, fontWeight:700, cursor:'pointer',
      }}>← Geri</button>
    </div>
  );

  const idx  = curIdx(order.status);
  const done = order.status === 'served';
  const items = order.items || [];

  return (
    <div style={{ background:C.bg, minHeight:'100vh',
      fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif', color:C.text }}>

      {/* ── Header ── */}
      <div style={{ background:C.surface, borderBottom:`1px solid ${C.border}`,
        padding:'16px 16px 14px', position:'sticky', top:0, zIndex:10 }}>
        <div style={{ maxWidth:480, margin:'0 auto', display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={() => navigate(-1)} style={{
            background:'rgba(255,255,255,.07)', border:'none', borderRadius:10,
            width:36, height:36, fontSize:16, color:C.text2, cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
          }}>←</button>
          <div>
            <div style={{ fontSize:16, fontWeight:800 }}>Sifarişin Statusu</div>
            <div style={{ fontSize:12, color:C.text2 }}>#{String(order.id||'').slice(-6).toUpperCase()}</div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth:480, margin:'0 auto', padding:'20px 16px 100px' }}>

        {/* ── Status banner ── */}
        <div style={{
          background: done ? `${C.primary}1a` : `${C.blue}15`,
          border:`1px solid ${done ? C.primary+'35' : C.blue+'28'}`,
          borderRadius:18, padding:'18px 20px', marginBottom:24, textAlign:'center',
        }}>
          <div style={{ fontSize:32, marginBottom:8 }}>{STEPS[idx].icon}</div>
          <div style={{ fontSize:18, fontWeight:800, marginBottom:4 }}>{STEPS[idx].label}</div>
          <div style={{ fontSize:13, color:C.text2 }}>{STEPS[idx].desc}</div>
        </div>

        {/* ── Timeline ── */}
        <div style={{ background:C.card, border:`1px solid ${C.border}`,
          borderRadius:18, padding:'20px', marginBottom:20 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase',
            letterSpacing:'.08em', marginBottom:16 }}>Status Addımları</div>

          {STEPS.map((step, i) => {
            const completed = i < idx;
            const active    = i === idx;
            return (
              <div key={step.key} style={{ display:'flex', gap:16 }}>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', flexShrink:0 }}>
                  <div style={{
                    width:32, height:32, borderRadius:'50%', flexShrink:0,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize: completed ? 13 : 10,
                    background: completed ? C.primary : active ? C.blue : 'rgba(255,255,255,.05)',
                    border: active ? `2px solid ${C.blue}` : 'none',
                    boxShadow: active ? `0 0 0 4px ${C.blue}1a` : 'none',
                    transition:'all .3s',
                  }}>
                    {completed
                      ? <span style={{ color:'#fff', fontWeight:800 }}>✓</span>
                      : active
                        ? <PulseRing />
                        : <span style={{ color:C.text3, fontSize:8 }}>●</span>
                    }
                  </div>
                  {i < STEPS.length-1 && (
                    <div style={{
                      width:2, flex:1, minHeight:28, margin:'4px 0',
                      background: completed ? C.primary : C.border,
                      transition:'background .3s',
                    }} />
                  )}
                </div>
                <div style={{ paddingTop:6, paddingBottom: i < STEPS.length-1 ? 24 : 0 }}>
                  <div style={{ fontSize:14, fontWeight: active ? 700 : 600,
                    color: completed ? C.primary : active ? C.text : C.text3,
                    transition:'color .3s' }}>{step.label}</div>
                  {active && <div style={{ fontSize:12, color:C.text2, marginTop:2 }}>{step.desc}</div>}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Items ── */}
        {items.length > 0 && (
          <div style={{ background:C.card, border:`1px solid ${C.border}`,
            borderRadius:18, padding:'20px', marginBottom:20 }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase',
              letterSpacing:'.08em', marginBottom:14 }}>Sifarişinizdəkilər</div>

            {items.map((item, i) => (
              <div key={item.id || i} style={{
                display:'flex', justifyContent:'space-between', alignItems:'center',
                paddingBottom: i<items.length-1 ? 10 : 0,
                marginBottom:  i<items.length-1 ? 10 : 0,
                borderBottom:  i<items.length-1 ? `1px solid ${C.border}` : 'none',
              }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ background:`${C.primary}18`, borderRadius:8,
                    padding:'3px 8px', fontSize:12, fontWeight:700, color:C.primary, flexShrink:0 }}>
                    {item.quantity}×
                  </div>
                  <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{item.itemName || item.name}</div>
                </div>
                <div style={{ fontSize:13, fontWeight:700, color:C.price, flexShrink:0 }}>
                  ₼{(Number(item.unitPrice || item.price || 0)*item.quantity).toFixed(2)}
                </div>
              </div>
            ))}

            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
              marginTop:14, paddingTop:14, borderTop:`1px solid ${C.border}` }}>
              <span style={{ fontSize:14, fontWeight:700, color:C.text2 }}>Cəmi:</span>
              <span style={{ fontSize:18, fontWeight:800, color:C.price }}>₼{Number(order.totalAmount||0).toFixed(2)}</span>
            </div>

            {order.notes && (
              <div style={{ marginTop:12, padding:'10px 12px',
                background:'rgba(255,255,255,.04)', borderRadius:10, fontSize:12, color:C.text2 }}>
                📝 {order.notes}
              </div>
            )}
          </div>
        )}

        {/* ── Waiter call ── */}
        {!done && (
          <div style={{ background:C.card, border:`1px solid ${C.border}`,
            borderRadius:18, padding:'20px', marginBottom:20 }}>
            <div style={{ fontSize:13, fontWeight:700, marginBottom:6 }}>Köməyə ehtiyacınız varmı?</div>
            <div style={{ fontSize:12, color:C.text2, marginBottom:14 }}>
              Ofisiantı çağırmaq üçün bu düyməyə basın.
            </div>
            <button onClick={handleCallWaiter}
              disabled={waiterLoading || waiterCooldown > 0}
              style={{
                width:'100%', padding:'13px',
                background: waiterCooldown>0 ? 'rgba(255,255,255,.05)' : `${C.warning}15`,
                border:`1px solid ${waiterCooldown>0 ? C.border : C.warning+'45'}`,
                borderRadius:12, fontSize:14, fontWeight:700,
                color: waiterCooldown>0 ? C.text3 : C.warning,
                cursor: (waiterLoading||waiterCooldown>0) ? 'not-allowed' : 'pointer',
                transition:'all .2s',
              }}>
              {waiterLoading ? '⟳ Göndərilir...'
               : waiterCooldown>0 ? `⏱ Növbəti çağırışa ${fmtCool(waiterCooldown)}`
               : '🔔 Ofisiant Çağır'}
            </button>
            {waiterCalled && waiterCooldown>0 && (
              <div style={{ textAlign:'center', fontSize:11, color:C.primary, marginTop:8 }}>
                ✓ Ofisiant çağırışınız alındı
              </div>
            )}
          </div>
        )}

        {/* ── Done ── */}
        {done && (
          <div style={{ background:`${C.primary}12`, border:`1px solid ${C.primary}28`,
            borderRadius:18, padding:'20px', marginBottom:20, textAlign:'center' }}>
            <div style={{ fontSize:32, marginBottom:8 }}>🎉</div>
            <div style={{ fontSize:16, fontWeight:800, color:C.primary, marginBottom:6 }}>Nuş olsun!</div>
            <div style={{ fontSize:13, color:C.text2 }}>Sifarişiniz tamamlandı. Təşəkkür edirik!</div>
          </div>
        )}

        {!done && (
          <div style={{ textAlign:'center', fontSize:11, color:C.text3 }}>↻ Hər 8 saniyədə avtomatik yenilənir</div>
        )}
      </div>

      {/* Brand footer */}
      <div style={{ textAlign:'center', padding:'12px 0 24px', fontSize:11, color:C.text3 }}>
        Baku Gate · <span style={{ color:C.primary, fontWeight:700 }}>by.Elvin</span>
      </div>

      <style>{`*{box-sizing:border-box;margin:0;padding:0}body{background:${C.bg}}button{font-family:inherit}`}</style>
    </div>
  );
}
